import { useState } from 'react'
import { Eye, EyeOff, LoaderCircle } from 'lucide-react'
import { formatUsPhone } from '../../core/index.js'
import { fetchContactAddress } from '../hooks.js'

/** The channels whose address a person may see whole: a phone number and an email. */
const REVEALABLE_CHANNELS: ReadonlySet<string> = new Set(['sms', 'email'])

interface RevealableAddressProps {
  workerId: string
  contact: { contactId: string; channel: string; address: string }
  className?: string
}

/**
 * A worker's phone number (or email) masked as the API lists it, `•••• 4321`,
 * with an eye beside it that shows it whole: the whole number is asked for
 * only when pressed, kept in this component alone and never in the query
 * cache, and the eye again hides it. A push device has nothing to show.
 */
export function RevealableAddress({ workerId, contact, className = '' }: RevealableAddressProps) {
  const [whole, setWhole] = useState<string | null>(null)
  const [shown, setShown] = useState(false)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const revealable = REVEALABLE_CHANNELS.has(contact.channel)

  async function toggle(): Promise<void> {
    if (shown) {
      setShown(false)
      return
    }
    if (whole !== null) {
      setShown(true)
      return
    }
    setLoading(true)
    setFailed(false)
    try {
      const answer = await fetchContactAddress(workerId, contact.contactId)
      setWhole(answer.channel === 'sms' ? formatUsPhone(answer.address) : answer.address)
      setShown(true)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }

  const label = shown ? 'Hide the number' : contact.channel === 'email' ? 'Show the whole email' : 'Show the whole number'
  const Icon = loading ? LoaderCircle : shown ? EyeOff : Eye
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span className={shown ? 'select-all font-mono' : ''}>{shown && whole !== null ? whole : contact.address}</span>
      {revealable && (
        <button type="button" onClick={() => void toggle()} disabled={loading} aria-label={label} title={label} aria-pressed={shown} className="text-marine hover:text-marine-dk disabled:opacity-40">
          <Icon aria-hidden="true" strokeWidth={2.25} className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      )}
      {failed && <span className="text-coral">could not be shown</span>}
    </span>
  )
}
